/**
 * Files attached to a chat question (#16101) — the rules both halves share.
 *
 * Pure and client-safe: the composer's staging hook
 * (`@/components/ai-chat/use-chat-attachments`) checks a dropped, pasted or
 * chosen file against these BEFORE uploading it, and the server
 * (`addAiChatAttachment`, `/api/ai-chat`) holds the same numbers again,
 * authoritatively, because a browser check is a courtesy and never a gate.
 *
 * ## Where a file lives
 *
 * At upload a file is owned by its UPLOADER, at
 * `(AI_CHAT_STAGING_ATTACHABLE_TYPE, <their Person.id>)` — resolvable the
 * moment it exists, which is #12890's lesson: a composer that staged under an
 * id nothing resolved stranded files. No conversation is created to hold it.
 * When a turn is sent the route re-points every attached row to
 * `(AI_CHAT_CONVERSATION_ATTACHABLE_TYPE, <the conversation the turn landed
 * in>)` and sets `Attachment.aiChatMessageId` to that user turn, so a file
 * staged on Home follows the question into whatever conversation receives it.
 *
 * ## What the model is given
 *
 * By {@link chatAttachmentKind}: an `image` is shown to the model as pixels,
 * a `document` is given as its extracted text under the attachment-text
 * budget, and anything `other` (a spreadsheet, slides, a video) is accepted
 * and NAMED to the model as not read, so it never denies a file exists.
 */

import { isHeicFile } from '@/lib/attachments/heic'
import {
  getMaxSizeForType,
  resolveFileMimeType,
} from '@/lib/storage/file-types'

/** Files one question may carry. */
export const MAX_CHAT_ATTACHMENTS_PER_TURN = 4

/**
 * Files one conversation may carry across all its turns. Images are re-sent
 * as pixels whenever the history is replayed, so this is also what bounds the
 * cost of a long conversation about photos.
 */
export const MAX_CHAT_ATTACHMENTS_PER_CONVERSATION = 12

/**
 * An abuse guard on the upload action: a person's staged (unsent, undeleted)
 * files created within {@link STAGED_CHAT_ATTACHMENT_WINDOW_MS}.
 */
export const MAX_STAGED_CHAT_ATTACHMENTS = 12

/**
 * How long a staged file stays sendable. Older staged rows are ignored by the
 * staging cap and refused by the route: a file abandoned in a closed tab is
 * private to its uploader and harmless, and no sweep is in scope.
 */
export const STAGED_CHAT_ATTACHMENT_WINDOW_MS = 24 * 60 * 60 * 1000

/**
 * The largest image shown to the model as pixels: the API's per-image cap,
 * which it measures on the BASE64 payload, not the file's bytes — so a JPEG
 * of about 3.75 MB is already at it. Compare {@link base64Length}.
 */
export const MAX_MODEL_IMAGE_BASE64_BYTES = 5 * 1024 * 1024

/**
 * The base64 image bytes one model request carries, across this turn and
 * every replayed one. The Messages API refuses a request over 32 MB, and a
 * conversation's images are re-sent on every turn, so without this a
 * conversation about a dozen phone photos would fail on every turn after it
 * crossed the line. The newest images are shown first; older ones are named
 * to the model as not shown. Room is left under the limit for the text.
 */
export const MAX_MODEL_IMAGE_BYTES_PER_REQUEST = 20 * 1024 * 1024

/** The size of `bytes` once base64-encoded. */
export function base64Length(bytes: number): number {
  return Math.ceil(bytes / 3) * 4
}

/**
 * The ids of the images the model is shown this request, newest first: each
 * within the API's per-image cap (on its base64 size), and together within
 * {@link MAX_MODEL_IMAGE_BYTES_PER_REQUEST}. `rows` is oldest first; an image
 * left out is named to the model as not shown.
 */
export function imagesWithinBudget(
  rows: readonly { id: string; mimeType: string; sizeBytes: number }[],
): Set<string> {
  const shown = new Set<string>()
  let remaining = MAX_MODEL_IMAGE_BYTES_PER_REQUEST
  for (const row of [...rows].reverse()) {
    if (chatAttachmentKind(row.mimeType) !== 'image') continue
    const encoded = base64Length(row.sizeBytes)
    if (encoded > MAX_MODEL_IMAGE_BASE64_BYTES || encoded > remaining) continue
    remaining -= encoded
    shown.add(row.id)
  }
  return shown
}

/**
 * The largest file a chat question takes, of any type: the most a server
 * action's body carries (`bodySizeLimit: '26mb'` in `next.config.js`). A
 * larger file would be refused by Next before the action could say why, so
 * the composer refuses it by name instead.
 */
export const MAX_CHAT_ATTACHMENT_BYTES = 25 * 1024 * 1024

/** The largest `mimeType` file a chat question takes. */
export function maxChatAttachmentBytes(mimeType: string): number {
  return Math.min(getMaxSizeForType(mimeType), MAX_CHAT_ATTACHMENT_BYTES)
}

/**
 * The route's refusal for any attachment id that does not resolve, whatever
 * the reason. Client-safe so a composer can recognise it: a send refused
 * with it would be refused again with the same files.
 */
export const CHAT_ATTACHMENT_NOT_FOUND = 'Attachment not found'

/** The type a HEIC photo is staged as: the composer converts it first. */
const HEIC_STAGED_MIME_TYPE = 'image/jpeg'

/**
 * The type `file` will be staged as, or null when the product does not take
 * it. A HEIC/HEIF photo counts as the JPEG the composer converts it to
 * (`normalizeImageFile`, #16099) before it uploads.
 */
export function stagedMimeType(file: CandidateFile): string | null {
  if (isHeicFile(file)) return HEIC_STAGED_MIME_TYPE
  return resolveFileMimeType(file.name, file.type)
}

/** The owner of a file that has been uploaded and not yet sent. */
export const AI_CHAT_STAGING_ATTACHABLE_TYPE = 'AiChatStaging'

/** The owner of a file that was sent with a turn in a conversation. */
export const AI_CHAT_CONVERSATION_ATTACHABLE_TYPE = 'AiChatConversation'

export const CHAT_ATTACHMENT_VIAS = ['button', 'drop', 'paste'] as const
export type ChatAttachmentVia = (typeof CHAT_ATTACHMENT_VIAS)[number]

export function isChatAttachmentVia(
  value: unknown,
): value is ChatAttachmentVia {
  return (CHAT_ATTACHMENT_VIAS as readonly unknown[]).includes(value)
}

/**
 * Where a file was attached — `ai_chat.attachment_added`'s `surface`. `home`
 * and `panel` are #16101's; the rest are the ask bars that hand their
 * question to the panel (#16102): Today's (both of them), the prep lens's,
 * the org page's and ActionBoard's.
 */
export const CHAT_ATTACHMENT_SURFACES = [
  'home',
  'panel',
  'today',
  'today_prep',
  'org_page',
  'actionboard',
] as const
export type ChatAttachmentSurface = (typeof CHAT_ATTACHMENT_SURFACES)[number]

export function isChatAttachmentSurface(
  value: unknown,
): value is ChatAttachmentSurface {
  return (CHAT_ATTACHMENT_SURFACES as readonly unknown[]).includes(value)
}

export type ChatAttachmentKind = 'image' | 'document' | 'other'

/** A file as the transcript shows it on the turn that carried it. */
export interface SentChatAttachment {
  id: string
  fileName: string
  mimeType: string
}

const IMAGE_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
])

const DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
])

/** How the model is given a stored file of `mimeType`. See the header. */
export function chatAttachmentKind(mimeType: string): ChatAttachmentKind {
  if (IMAGE_TYPES.has(mimeType)) return 'image'
  if (DOCUMENT_TYPES.has(mimeType)) return 'document'
  return 'other'
}

/** A file as the browser describes it; `File` satisfies this. */
export interface CandidateFile {
  name: string
  type: string
  size: number
}

export type StagingRejectionReason = 'unsupported' | 'too_large' | 'folder'

export interface StagingPlan<F extends CandidateFile> {
  /** In order, at most what the per-turn cap leaves room for. */
  accepted: F[]
  rejected: Array<{ name: string; reason: StagingRejectionReason }>
  /** Supported files turned away only because the turn was full. */
  skippedOverCap: number
}

/**
 * Which of `files` to stage, given `alreadyStaged` chips on the composer.
 *
 * A type the product does not take, a file over its type's size limit, and a
 * dropped folder (a zero-byte entry with no type) are rejected by name; the
 * rest are accepted up to {@link MAX_CHAT_ATTACHMENTS_PER_TURN}. The server
 * decides the type again from the bytes, so this only spares an upload that
 * would certainly be refused.
 */
export function planChatAttachmentStaging<F extends CandidateFile>(
  files: readonly F[],
  alreadyStaged: number,
): StagingPlan<F> {
  const plan: StagingPlan<F> = { accepted: [], rejected: [], skippedOverCap: 0 }
  let room = Math.max(0, MAX_CHAT_ATTACHMENTS_PER_TURN - alreadyStaged)
  for (const file of files) {
    if (file.size === 0 && file.type === '') {
      plan.rejected.push({ name: file.name, reason: 'folder' })
      continue
    }
    const mimeType = stagedMimeType(file)
    if (mimeType === null) {
      plan.rejected.push({ name: file.name, reason: 'unsupported' })
      continue
    }
    // A HEIC's size is checked after conversion: the JPEG is retried at
    // lower qualities to fit, and the server checks the result.
    if (!isHeicFile(file) && file.size > maxChatAttachmentBytes(mimeType)) {
      plan.rejected.push({ name: file.name, reason: 'too_large' })
      continue
    }
    if (room === 0) {
      plan.skippedOverCap += 1
      continue
    }
    room -= 1
    plan.accepted.push(file)
  }
  return plan
}

/**
 * ONE message for everything a staging turned away, or null for none: each
 * rejected file by name, and how many were skipped because the question was
 * full. One toast per drop, not one per file.
 */
export function stagingRejectionMessage(
  plan: StagingPlan<CandidateFile>,
): string | null {
  const parts: string[] = []
  if (plan.rejected.length > 0) {
    const names = plan.rejected.map(r => r.name).join(', ')
    parts.push(
      `Can't attach ${names}: unsupported type, too large, or a folder.`,
    )
  }
  if (plan.skippedOverCap > 0) {
    parts.push(
      `A question can carry ${MAX_CHAT_ATTACHMENTS_PER_TURN} files; ${plan.skippedOverCap} more were not attached.`,
    )
  }
  return parts.length > 0 ? parts.join(' ') : null
}
